import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Popup from "../../../components/Popup";
import { FormattedMessage, useIntl } from "react-intl";
import cmmsMessages from "../../cmms/cmmsMessages";
import sparePartMessages from "../../cmmsSpareParts/messages";
import messages from "../messages";
import AxionControls from "../../../components/controls/AxionControls";
import { createMaterialUsage, updateMaterialUsage } from "../../../fetchers/cmmsWorkOrder";
import { getSpareParts } from "../../../fetchers/cmmsSparePart";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { formatCmmsError } from "../../cmms/components/CmmsStates";
import { formatQuantity } from "../../cmms/cmmsFormat";
import {
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getOutlinedButtonSx,
  getRadioSx,
} from "../../cmms/assetHierarchyStyles";
import FieldError from "../../cmmsSpareParts/components/FieldError";
import {
  buildSparePartOptions,
  findSparePartOption,
  getStockShortfall,
} from "../../cmmsSpareParts/sparePartPicker";
import {
  MaterialSource,
  buildMaterialPayload,
  clearStampedFields,
  resolveMaterialSource,
  validateMaterialForm,
} from "../materialSource";

const emptyForm = {
  sparePartId: null,
  partNumber: "",
  description: "",
  quantity: 1,
  unit: "",
  unitCost: "",
};

/*
 * Book what a job consumed, from the register or as free text.
 *
 * The two sources are a choice rather than an empty field. Pointing at the register is what
 * makes the consumption row the outgoing side of the stock balance; free text is for the bolt
 * out of the drawer that will never have a register row. Neither is the incomplete version of
 * the other, and the radio says so.
 */
export default function MaterialUsageFormDialog({ open, onClose, workOrderId, material, companyKey, onSuccess }) {
  const theme = useTheme();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const intl = useIntl();
  const queryClient = useQueryClient();
  const isEdit = Boolean(material);

  const [source, setSource] = useState(MaterialSource.Registered);
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [showOverride, setShowOverride] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Same key and filter the register page uses, so whichever is opened first warms the other.
  // Gated on `open`: a work order whose materials tab is never touched fetches nothing.
  const sparePartsQuery = useQuery(
    ["cmmsSpareParts", companyKey, false],
    () => getSpareParts(companyKey, false),
    { enabled: open && Boolean(companyKey), staleTime: 300000 },
  );

  const options = useMemo(
    () => buildSparePartOptions(sparePartsQuery.data, intl),
    [sparePartsQuery.data, intl],
  );

  const selectedPart = useMemo(
    () => (sparePartsQuery.data ?? []).find((part) => part.id === form.sparePartId) ?? null,
    [sparePartsQuery.data, form.sparePartId],
  );

  useEffect(() => {
    if (!open) return;
    if (isEdit && material) {
      setSource(resolveMaterialSource(material));
      setForm({
        sparePartId: material.sparePartId ?? null,
        partNumber: material.partNumber ?? "",
        description: material.description ?? "",
        quantity: material.quantity ?? 0,
        unit: material.unit ?? "",
        unitCost: material.unitCost ?? "",
      });
      // An existing registered row already carries the stamped text, so the fields are not
      // blank and hiding them would hide what the row actually says.
      setShowOverride(resolveMaterialSource(material) === MaterialSource.Registered);
    } else {
      setSource(MaterialSource.Registered);
      setForm(emptyForm);
      setShowOverride(false);
    }
    setErrors({});
  }, [open, isEdit, material]);

  const handleField = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const handleTextChange = (event) => {
    const { name, id, value } = event.target;
    handleField(name || id, value);
  };

  /*
   * Switching to free text drops the part id from the form as well as from the payload.
   *
   * The text is deliberately kept: whatever the register stamped onto an existing row is the
   * best starting point for describing the same consumption by hand, and clearing it would
   * make the user retype what is already true.
   */
  const handleSourceChange = (next) => {
    setSource(next);
    setErrors({});
    if (next === MaterialSource.FreeText) {
      handleField("sparePartId", null);
      setShowOverride(false);
    }
  };

  /*
   * Picking a different part throws away what the previous one stamped.
   *
   * On an edited row those fields arrive filled, so without this the row would keep part A's
   * number, name and unit while pointing at part B. The server only fills blanks, so it cannot
   * correct it; blanking here is what lets StampFromSparePartAsync stay the single copy of the
   * rule. Picking the same part again is not a change and leaves an in-progress override alone.
   */
  const handleSparePartChange = (option) => {
    const nextId = option?.id ?? null;
    setForm((current) => (nextId === current.sparePartId
      ? current
      : { ...clearStampedFields(current), sparePartId: nextId }));
  };

  const isRegistered = source === MaterialSource.Registered;
  const shortfall = isRegistered ? getStockShortfall(selectedPart, form.quantity) : null;

  const partSummary = selectedPart
    ? intl.formatMessage(
        selectedPart.unitCost === null || selectedPart.unitCost === undefined
          ? sparePartMessages.pickerSummary
          : sparePartMessages.pickerSummaryWithCost,
        {
          name: selectedPart.name,
          unit: selectedPart.unit,
          unitCost: formatQuantity(intl, selectedPart.unitCost, 2),
        },
      )
    : null;

  const handleSubmit = async () => {
    const next = validateMaterialForm(form, source, intl);
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSubmitting(true);
    try {
      const payload = buildMaterialPayload(form, source, { companyKey });

      if (isEdit) {
        await updateMaterialUsage(workOrderId, material.id, payload);
        pushNotification(intl.formatMessage(messages.toastMaterialUpdated));
      } else {
        await createMaterialUsage(workOrderId, payload);
        pushNotification(intl.formatMessage(messages.toastMaterialAdded));
      }

      // The consumption row is the outgoing side of the balance, so booking one moves a number
      // the spare parts page is showing. Without this the register keeps a figure that stopped
      // being true the moment this dialog was saved.
      await queryClient.invalidateQueries({ queryKey: ["cmmsSpareParts"] });

      onSuccess?.();
      onClose?.();
    } catch (error) {
      // As it comes: a retired part answers CMMS_SPARE_PART_IS_INACTIVE and names itself, which
      // no fallback of ours can.
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.materialsSaveFailed)));
    } finally {
      setSubmitting(false);
    }
  };

  const registerIsEmpty = !sparePartsQuery.isInitialLoading
    && !sparePartsQuery.error
    && options.length === 0;

  return (
    <Popup
      openPopup={open}
      setOpenPopup={(value) => { if (!value) onClose?.(); }}
      maxWidth="sm"
      title={intl.formatMessage(isEdit ? messages.materialsEditDialogTitle : messages.materialsAddDialogTitle)}
    >
      <Box sx={{ display: "grid", gap: 2, p: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            <FormattedMessage {...messages.materialsSource} />
          </Typography>
          <RadioGroup row value={source} onChange={(event) => handleSourceChange(event.target.value)}>
            <FormControlLabel
              value={MaterialSource.Registered}
              control={<Radio size="small" sx={getRadioSx(cmmsColors)} />}
              label={intl.formatMessage(messages.materialsSourceRegistered)}
            />
            <FormControlLabel
              value={MaterialSource.FreeText}
              control={<Radio size="small" sx={getRadioSx(cmmsColors)} />}
              label={intl.formatMessage(messages.materialsSourceFreeText)}
            />
          </RadioGroup>
        </Box>

        {isRegistered &&
          <Box>
            <AxionControls.AxionAutocomplete
              name="sparePartId"
              label={intl.formatMessage(messages.materialsSparePart)}
              options={options}
              value={findSparePartOption(options, form.sparePartId)}
              onChange={(_event, option) => handleSparePartChange(option)}
              disabled={sparePartsQuery.isInitialLoading}
              error={errors.sparePartId}
            />
            {partSummary &&
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                <FormattedMessage {...messages.materialsFromRegister} values={{ summary: partSummary }} />
              </Typography>
            }
            {registerIsEmpty &&
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.5 }}>
                <FormattedMessage {...messages.materialsRegisterEmpty} />
              </Typography>
            }
            {sparePartsQuery.error &&
              <Typography variant="caption" sx={{ color: "error.main", display: "block", mt: 0.5 }}>
                {formatCmmsError(sparePartsQuery.error, intl.formatMessage(messages.materialsRegisterLoadFailed))}
              </Typography>
            }
          </Box>
        }

        {!isRegistered &&
          <AxionControls.AxionTextBox
            name="partNumber"
            label={intl.formatMessage(messages.materialsPartNumber)}
            value={form.partNumber}
            onChange={handleTextChange}
          />
        }

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <AxionControls.AxionTextBox
              name="quantity"
              label={intl.formatMessage(messages.materialsQuantityRequired)}
              value={form.quantity}
              onChange={handleTextChange}
            />
            <FieldError message={errors.quantity} />
          </Box>
          {!isRegistered &&
            <Box sx={{ flex: 1 }}>
              <AxionControls.AxionTextBox
                name="unit"
                label={intl.formatMessage(messages.materialsUnit)}
                value={form.unit}
                onChange={handleTextChange}
              />
              <FieldError message={errors.unit} />
            </Box>
          }
        </Stack>

        {/*
          * Advisory, and worded so. The balance is what the register answered when this list
          * loaded, and it is never a reason to refuse the booking: the shelf can be wrong —
          * that is what a stocktake settles — and refusing to record what was actually used
          * would corrupt the history to protect a number.
          */}
        {shortfall !== null &&
          <Alert severity="warning">
            <FormattedMessage
              {...messages.materialsShortfall}
              values={{
                onHand: formatQuantity(intl, selectedPart.quantityOnHand ?? 0, 2),
                wanted: formatQuantity(intl, form.quantity, 2),
                resulting: formatQuantity(intl, Number(selectedPart.quantityOnHand ?? 0) - Number(form.quantity), 2),
              }}
            />
          </Alert>
        }

        {!isRegistered &&
          <>
            <Box>
              <AxionControls.AxionTextBox
                name="description"
                label={intl.formatMessage(messages.fieldDescriptionRequired)}
                value={form.description}
                onChange={handleTextChange}
              />
              <FieldError message={errors.description} />
            </Box>
            <Box>
              <AxionControls.AxionTextBox
                name="unitCost"
                label={intl.formatMessage(messages.materialsUnitCost)}
                value={form.unitCost}
                onChange={handleTextChange}
              />
              <FieldError message={errors.unitCost} />
            </Box>
          </>
        }

        {/*
          * Closed by default on a new row, and the fields under it stay empty.
          *
          * StampFromSparePartAsync fills whatever the caller leaves blank, and keeping that the
          * only copy of the rule is why nothing is prefilled here — a client-side copy has no
          * good answer to "picked part A, edited the description, then picked part B".
          *
          * An edited row is the exception that used to break it: those fields arrive already
          * stamped, so the case the emptiness was meant to rule out could happen after all.
          * handleSparePartChange blanks them again on every change of part.
          */}
        {isRegistered &&
          <Box>
            <Link
              component="button"
              type="button"
              underline="none"
              onClick={() => setShowOverride((current) => !current)}
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}
            >
              {showOverride ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              <FormattedMessage {...messages.materialsOverrideToggle} />
            </Link>
            <Collapse in={showOverride}>
              <Box sx={{ display: "grid", gap: 2, mt: 1 }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  <FormattedMessage {...messages.materialsOverrideHint} />
                </Typography>
                {/*
                  * On screen deliberately. It was always part of the payload, but in register mode it
                  * was the one stamped field with nowhere on screen to show it — so a stale value
                  * could be saved with no way to see it, let alone fix it.
                  */}
                <AxionControls.AxionTextBox
                  name="partNumber"
                  label={intl.formatMessage(messages.materialsPartNumber)}
                  value={form.partNumber}
                  onChange={handleTextChange}
                />
                <AxionControls.AxionTextBox
                  name="description"
                  label={intl.formatMessage(messages.fieldDescription)}
                  value={form.description}
                  onChange={handleTextChange}
                />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <AxionControls.AxionTextBox
                      name="unit"
                      label={intl.formatMessage(messages.materialsUnit)}
                      value={form.unit}
                      onChange={handleTextChange}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <AxionControls.AxionTextBox
                      name="unitCost"
                      label={intl.formatMessage(messages.materialsUnitCost)}
                      value={form.unitCost}
                      onChange={handleTextChange}
                    />
                    <FieldError message={errors.unitCost} />
                  </Box>
                </Stack>
              </Box>
            </Collapse>
          </Box>
        }

        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button variant="outlined" onClick={onClose} disabled={submitting} sx={getOutlinedButtonSx(cmmsColors)}>
            <FormattedMessage {...cmmsMessages.cancel} />
          </Button>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting} sx={getContainedButtonSx(cmmsColors)}>
            <FormattedMessage {...(isEdit ? cmmsMessages.save : cmmsMessages.add)} />
          </Button>
        </Stack>
      </Box>
      <AxionControls.AxionBackdrop open={submitting} />
    </Popup>
  );
}
